import React, { useState } from 'react'
import classNames from 'classnames'
import { isEmpty, isNil, xor } from 'lodash'
import {
  IconCheck,
  IconChevronDown,
  IconPencil,
  IconPlus,
  IconTrash,
  IconX,
} from '@tabler/icons-react'

import { Banner } from '@/components/banners/Banner'
import { Button } from '@/components/buttons/Button'
import { Card } from '@/components/cards/Card'
import NoData from '@/components/NoData'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { Accordion } from '@/components/accordions/Accordion'
import { SearchableDropdown } from '@/components/dropdowns/SearchableDropdown'
import { MultiSelect } from '@/components/forms/MultiSelect'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { Pill } from '@/components/pills/Pill'
import { FormField } from '@/components/forms/FormField'
import { TextArea } from '@/components/forms/TextArea'
import { TextInput } from '@/components/forms/TextInput'

const MAX_CRITERION_POINTS = 100

// Mirrors `criteriaPointsValidationSchema` in QACopilot.helpers — the manual builder's rule for
// the same field — down to the wording, so a criterion cannot be worth 4.32, -5 or 12321892934
// points here and something else over there.
export const getPointsError = (points) => {
  const trimmedPoints = String(points ?? '').trim()

  if (isEmpty(trimmedPoints)) {
    return 'Points are required'
  }
  if (!/^-?\d+$/.test(trimmedPoints)) {
    return 'Points must be a whole number'
  }
  if (Number(trimmedPoints) < 1) {
    return 'Points must be greater than 0'
  }
  if (Number(trimmedPoints) > MAX_CRITERION_POINTS) {
    return `Points must be less than or equal to ${MAX_CRITERION_POINTS}`
  }

  return null
}

const INCLUSIVITY_OPTIONS = [
  { value: 'true', label: 'Includes' },
  { value: 'false', label: 'Excludes' },
]

const CALL_LENGTH_LIMITS = [
  {
    stateKey: 'minDurationMinutes',
    direction: 'is at least',
    placeholder: 'Set minimum call duration in minutes',
    unitLabel: 'minute',
  },
  {
    stateKey: 'maxDurationMinutes',
    direction: 'is at most',
    placeholder: 'Set maximum call duration in minutes',
    unitLabel: 'minutes',
  },
]

const ELIGIBILITY_FILTERS = [
  {
    label: 'Agents',
    valueKey: 'agents',
    inclusivityKey: 'usersInclusive',
    placeholder: 'Select agents (optional)',
    inclusivityTestId: 'users_inclusive-dropdown',
  },
  {
    label: 'Tags',
    valueKey: 'tags',
    inclusivityKey: 'tagsInclusive',
    placeholder: 'Select tags (optional)',
    inclusivityTestId: 'tags_inclusive-dropdown',
  },
  {
    label: 'Playbook',
    valueKey: 'playbooks',
    hint: 'matches',
    placeholder: 'Select Playbooks (optional)',
  },
  {
    label: 'VoIP Campaigns',
    valueKey: 'voipCampaigns',
    inclusivityKey: 'voipCampaignsInclusive',
    placeholder: 'Select VoIP Campaigns (optional)',
    inclusivityTestId: 'voip_campaigns_inclusive-dropdown',
  },
]

export const GuidedStepNav = ({ steps, currentStep, completedSteps, onStepSelect }) => (
  <nav
    className="qa-copilot-guided-nav flex-column small-gap"
    aria-label="Setup steps"
    data-testid="guided-step-nav"
  >
    <p className="qa-copilot-guided-nav__eyebrow qa-copilot-guided-eyebrow uppercase muted-text bold">
      Setup
    </p>
    {steps.map((step, index) => {
      const isCurrent = step.key === currentStep
      const isComplete = !isCurrent && completedSteps.includes(step.key)

      return (
        <button
          key={step.key}
          type="button"
          className={classNames(
            'qa-copilot-guided-nav__step qa-copilot-guided-plain-button flex-align-center full-width medium-gap',
            {
              current: isCurrent,
              complete: isComplete,
            }
          )}
          disabled={!isComplete}
          onClick={() => onStepSelect(step.key)}
          aria-current={isCurrent ? 'step' : undefined}
          data-testid={`guided-nav-${step.key}`}
        >
          <Pill
            circular
            brand={isCurrent || isComplete}
            emphasized={isCurrent}
            outlined={isComplete}
            aria-hidden="true"
          >
            {isComplete ? <IconCheck /> : index + 1}
          </Pill>
          <span className="flex-column">
            <span className="bold">{step.title}</span>
            <span className="small-text muted-text">{step.subtitle}</span>
          </span>
        </button>
      )
    })}
  </nav>
)

export const GuidedStepHeader = ({ stepNumber, totalSteps, title, description }) => (
  <header className="qa-copilot-guided-step-header">
    <p className="qa-copilot-guided-eyebrow uppercase muted-text bold no-margin">
      {`Step ${stepNumber} of ${totalSteps}`}
    </p>
    <h2>{title}</h2>
    {!isEmpty(description) && <p className="muted-text no-margin">{description}</p>}
  </header>
)

export const GuidedEligibilityStep = ({
  eligibility,
  updateEligibility,
  eligibilityOptions = {},
  eligibilityLoading = {},
  editable = false,
  editingNotice,
  lockedUntilEdit = false,
  originalEligibility,
  validationError,
}) => {
  const [filtersExpanded, setFiltersExpanded] = useState(() =>
    ELIGIBILITY_FILTERS.some(({ valueKey }) => !isEmpty(eligibility[valueKey]))
  )
  const [isUnlocked, setIsUnlocked] = useState(!lockedUntilEdit)
  const [snapshot, setSnapshot] = useState(originalEligibility ?? null)
  const isLocked = !isUnlocked

  const editControls = isUnlocked ? (
    <span className="flex-align-center small-gap">
      <Button
        small
        square
        basic
        aria-label="Cancel eligibility changes"
        tooltip="Cancel"
        onClick={() => {
          updateEligibility(snapshot)
          setIsUnlocked(false)
        }}
        data-testid="guided-eligibility-cancel"
      >
        <IconX />
      </Button>
      <Button
        small
        square
        brand
        aria-label="Confirm eligibility changes"
        tooltip="Done"
        onClick={() => setIsUnlocked(false)}
        data-testid="guided-eligibility-confirm"
      >
        <IconCheck />
      </Button>
    </span>
  ) : (
    <Button
      text
      small
      onClick={() => {
        setSnapshot(eligibility)
        setIsUnlocked(true)
      }}
      data-testid="guided-eligibility-edit"
    >
      <IconPencil />
      Edit
    </Button>
  )

  return (
    <Card
      className="qa-copilot-guided-panel"
      title="Eligibility Rules"
      buttons={editable ? editControls : undefined}
      data-testid="guided-step-eligibility"
    >
      {isLocked ? (
        <p className="muted-text no-margin">Click Edit to change the rules and filters</p>
      ) : (
        <div className="field-stack">
          {!isNil(editingNotice) && (
            <Banner informative shrunkHeight data-testid="guided-eligibility-editing-notice">
              {editingNotice}
            </Banner>
          )}
          <FormField>
            <label htmlFor="guided-eligibility-review-rules">
              How do you determine what calls to review?
            </label>
            <TextArea
              id="guided-eligibility-review-rules"
              className="full-width"
              value={eligibility.reviewRules}
              disabled={isLocked}
              onChange={(event) => updateEligibility({ reviewRules: event.target.value })}
              placeholder="e.g. Agent confirmed the customer's interest and walked through pricing"
              data-testid="guided-eligibility-review-rules"
            />
          </FormField>

          <FormField>
            <label htmlFor="guided-eligibility-exclusions">
              What, if any, types of calls should be excluded from being graded?
            </label>
            <TextArea
              id="guided-eligibility-exclusions"
              className="full-width"
              value={eligibility.exclusions}
              disabled={isLocked}
              onChange={(event) => updateEligibility({ exclusions: event.target.value })}
              placeholder="e.g. Transferred calls, voicemails, calls that end abruptly"
              data-testid="guided-eligibility-exclusions"
            />
          </FormField>

          <Accordion
            styled
            className="mtl"
            id="guided-optional-filters"
            title="Optional filters"
            expanded={filtersExpanded}
            handleToggle={() => setFiltersExpanded((expanded) => !expanded)}
          >
            <div className="multiple-field-container">
              {CALL_LENGTH_LIMITS.map((limit, index) => (
                <React.Fragment key={limit.stateKey}>
                  {index > 0 && (
                    <Pill small emphasized>
                      AND
                    </Pill>
                  )}
                  <div className="non-repeater-grid">
                    <div className="label-style">Call Length</div>
                    <div className="muted-text">{limit.direction}</div>
                    <div className="flex-align-center gap">
                      <FormField className="qa-copilot-settings-form-min-max-duration-minutes">
                        <TextInput
                          type="number"
                          min={1}
                          placeholder={limit.placeholder}
                          value={eligibility[limit.stateKey]}
                          disabled={isLocked}
                          onChange={(event) =>
                            updateEligibility({ [limit.stateKey]: event.target.value })
                          }
                        />
                      </FormField>
                      <div className="muted-text">{limit.unitLabel}</div>
                    </div>
                  </div>
                </React.Fragment>
              ))}

              {ELIGIBILITY_FILTERS.map((filter) => (
                <React.Fragment key={filter.valueKey}>
                  <Pill small emphasized>
                    AND
                  </Pill>
                  <div className="non-repeater-grid">
                    <div className="label-style">{filter.label}</div>
                    {filter.inclusivityKey ? (
                      <SearchableDropdown
                        hideSearch
                        options={INCLUSIVITY_OPTIONS}
                        selected={eligibility[filter.inclusivityKey]}
                        isDisabled={isLocked}
                        handleSelect={(value) =>
                          updateEligibility({ [filter.inclusivityKey]: value })
                        }
                        buttonProps={{ xs: true, basic: true, className: 'mr' }}
                        dataTestId={filter.inclusivityTestId}
                      />
                    ) : (
                      <div className="muted-text">{filter.hint}</div>
                    )}
                    <div data-testid={`guided-${filter.valueKey}-multi-select`}>
                      <MultiSelect
                        options={eligibilityOptions[filter.valueKey] || []}
                        value={eligibility[filter.valueKey]}
                        onChange={(options) =>
                          updateEligibility({ [filter.valueKey]: options || [] })
                        }
                        placeholder={filter.placeholder}
                        loading={eligibilityLoading[filter.valueKey]}
                        disabled={isLocked}
                        condenseOptions={false}
                        showFullOptions
                      />
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </Accordion>
        </div>
      )}

      {!isEmpty(validationError) && <ErrorMessage content={validationError} />}
    </Card>
  )
}

// Confirming the delete against the button that raised it. A section takes its criteria with it and
// none of this is undoable, so it is worth a second look — but a full-screen dialog over a list the
// user is actively editing interrupts more than it protects. Nothing worth losing (an empty section,
// a criterion with nothing typed in it) still deletes on the first click.
const GuidedDeleteButton = ({ label, needsConfirmation, onConfirm, dataTestId }) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  const deleteButton = (
    <Button
      small
      square
      basic
      aria-label={label}
      tooltip={needsConfirmation ? undefined : label}
      onClick={needsConfirmation ? undefined : onConfirm}
      data-testid={dataTestId}
    >
      <IconTrash />
    </Button>
  )

  if (!needsConfirmation) {
    return deleteButton
  }

  return (
    <InteractiveTooltip
      clickable
      theme="light"
      placement="bottom-end"
      open={isConfirmOpen}
      setOpen={setIsConfirmOpen}
      content={
        <div className="flex-align-center small-gap">
          <Button xs onClick={() => setIsConfirmOpen(false)} data-testid="guided-delete-cancel">
            Cancel
          </Button>
          <Button
            xs
            destructive
            onClick={() => {
              setIsConfirmOpen(false)
              onConfirm()
            }}
            data-testid="guided-delete-confirm"
          >
            Delete
          </Button>
        </div>
      }
    >
      {deleteButton}
    </InteractiveTooltip>
  )
}

const AddSectionInlineEntry = ({ onAdd, disabled }) => {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')

  const handleCancel = () => {
    setEditing(false)
    setValue('')
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    if (!value.trim()) {
      return
    }
    const didAddSection = onAdd(value.trim())

    if (didAddSection === false) {
      return
    }

    handleCancel()
  }

  if (!editing) {
    return (
      <Button
        basic
        disabled={disabled}
        onClick={() => setEditing(true)}
        data-testid="guided-add-section-button"
      >
        <IconPlus />
        Add section
      </Button>
    )
  }

  return (
    <form className="flex-align-center small-gap" onSubmit={handleSubmit}>
      <TextInput
        autoFocus
        type="text"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder="Section name"
        data-testid="guided-add-section-input"
      />
      <Button small square basic onClick={handleCancel} type="button">
        <IconX />
      </Button>
      <Button
        small
        square
        brand
        type="submit"
        disabled={!value.trim()}
        data-testid="guided-add-section-submit"
      >
        <IconCheck />
      </Button>
    </form>
  )
}

const GuidedCriterionForm = ({ criteriaRow, onUpdate }) => {
  const pointsError = getPointsError(criteriaRow.points)
  const hasExistingPrompt = !isNil(criteriaRow.scid)

  return (
    <div className="field-stack mt">
      {hasExistingPrompt && (
        <Banner informative shrunkHeight>
          This criterion already has scoring rules. Any changes here will replace the current rules
        </Banner>
      )}
      <FormField required>
        <label htmlFor="guided-criterion-name">Criteria Name</label>
        <TextInput
          id="guided-criterion-name"
          type="text"
          value={criteriaRow.criterionName}
          onChange={(event) => onUpdate('criterionName', event.target.value)}
          data-testid="guided-custom-criterion-name"
        />
      </FormField>

      <FormField required>
        <label htmlFor="guided-pass-scenario">What counts as a pass?</label>
        <TextArea
          id="guided-pass-scenario"
          className="full-width"
          value={criteriaRow.passScenario}
          onChange={(event) => onUpdate('passScenario', event.target.value)}
          placeholder="Describe the ideal behavior..."
          data-testid="guided-custom-pass-scenario"
        />
      </FormField>

      <FormField required>
        <label htmlFor="guided-fail-scenario">What counts as a fail?</label>
        <TextArea
          id="guided-fail-scenario"
          className="full-width"
          value={criteriaRow.failScenario}
          onChange={(event) => onUpdate('failScenario', event.target.value)}
          placeholder="Describe what would cause this criterion to fail..."
          data-testid="guided-custom-fail-scenario"
        />
      </FormField>

      <FormField required>
        <label htmlFor="guided-not-applicable-scenario">
          When is this criterion not applicable?
        </label>
        <TextArea
          id="guided-not-applicable-scenario"
          className="full-width"
          value={criteriaRow.notApplicableScenario}
          onChange={(event) => onUpdate('notApplicableScenario', event.target.value)}
          placeholder="e.g., Outbound calls, calls under 30 seconds..."
          data-testid="guided-custom-not-applicable-scenario"
        />
      </FormField>

      <FormField required error={!isNil(pointsError)}>
        <label htmlFor="guided-points">Points</label>
        <TextInput
          id="guided-points"
          type="number"
          min={1}
          max={MAX_CRITERION_POINTS}
          step={1}
          error={!isNil(pointsError)}
          value={criteriaRow.points}
          onChange={(event) => onUpdate('points', event.target.value)}
          data-testid="guided-custom-points"
        />
        {!isNil(pointsError) && (
          <ErrorMessage content={pointsError} data-testid="guided-points-error" />
        )}
      </FormField>
    </div>
  )
}

export const parsePositiveInteger = (value, fallback = 1) => {
  const parsedValue = Number.parseInt(String(value || '').trim(), 10)
  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback
}

// "Nothing typed yet" — a row added by mistake, which deleting costs the user nothing. Points
// are excluded on purpose: every new row is seeded with a default, so they are never blank.
const hasNoScenarios = (criteriaRow) =>
  criteriaRow.passScenario.trim() === '' &&
  criteriaRow.failScenario.trim() === '' &&
  criteriaRow.notApplicableScenario.trim() === ''

export const isCriteriaRowEmpty = (criteriaRow) =>
  criteriaRow.criterionName.trim() === '' && hasNoScenarios(criteriaRow)

export const keepsExistingPrompt = (criteriaRow) =>
  !isNil(criteriaRow.scid) && hasNoScenarios(criteriaRow)

export const isCriteriaRowComplete = (criteriaRow) =>
  criteriaRow.criterionName.trim() !== '' &&
  isNil(getPointsError(criteriaRow.points)) &&
  (keepsExistingPrompt(criteriaRow) ||
    (criteriaRow.passScenario.trim() !== '' &&
      criteriaRow.failScenario.trim() !== '' &&
      criteriaRow.notApplicableScenario.trim() !== ''))

const sumPoints = (criteriaRows) =>
  criteriaRows.reduce(
    (runningTotal, criteriaRow) => runningTotal + parsePositiveInteger(criteriaRow.points, 0),
    0
  )

export const pluralize = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`

const criteriaForSection = (criteriaRows, sectionId) =>
  criteriaRows.filter((criteriaRow) => criteriaRow.sectionId === sectionId)

export const GuidedReviewStructure = ({ sections, criteriaRows }) => {
  const [expandedSectionIds, setExpandedSectionIds] = useState([])

  const toggleSection = (sectionId) => setExpandedSectionIds((ids) => xor(ids, [sectionId]))

  return (
    <div className="flex-column small-gap">
      {sections.map((section) => {
        const sectionCriteria = criteriaForSection(criteriaRows, section.id)
        const isExpanded = expandedSectionIds.includes(section.id)

        return (
          <Card compact key={section.id} className="flex-column small-gap no-margin">
            <button
              type="button"
              className="qa-copilot-guided-plain-button full-width flex-align-center small-gap"
              onClick={() => toggleSection(section.id)}
              aria-expanded={isExpanded}
              data-testid={`guided-review-section-${section.id}`}
            >
              <IconChevronDown
                className={classNames('icon-svg qa-copilot-guided-chevron', {
                  expanded: isExpanded,
                })}
              />
              <strong className="flex-item text-overflow">{section.name}</strong>
              <Pill small>{pluralize(sectionCriteria.length, 'criterion', 'criteria')}</Pill>
              <Pill small informative>{`${sumPoints(sectionCriteria)} pts`}</Pill>
            </button>

            {isExpanded &&
              (isEmpty(sectionCriteria) ? (
                <p className="muted-text no-margin">No criteria in this section.</p>
              ) : (
                <div className="flex-column small-gap">
                  {sectionCriteria.map((criteriaRow) => (
                    <Card
                      compact
                      key={criteriaRow.rowId}
                      className="qa-copilot-guided-panel flex-align-center small-gap no-margin"
                    >
                      <span className="flex-item text-overflow">
                        {criteriaRow.criterionName.trim() || 'Untitled Criterion'}
                      </span>
                      <Pill small>{`${parsePositiveInteger(criteriaRow.points)} pts`}</Pill>
                    </Card>
                  ))}
                </div>
              ))}
          </Card>
        )
      })}
    </div>
  )
}

export const GuidedStructureStep = ({
  sections,
  criteriaRows,
  selectedCriteriaRowId,
  onCriteriaSelect,
  onAddCriterionToSection,
  onRemoveCriterion,
  onReplaceCriterion,
  onUpdateSelectedCriteria,
  onAddSection,
  onRemoveSection,
  onUpdateSection,
  validationError,
}) => {
  const [editingSectionId, setEditingSectionId] = useState(null)
  const [editingSectionValue, setEditingSectionValue] = useState('')
  const [criterionSnapshot, setCriterionSnapshot] = useState(null)
  const [activeSectionId, setActiveSectionId] = useState(sections[0]?.id ?? null)
  const activeSection =
    sections.find(({ id }) => id === activeSectionId) ?? sections[sections.length - 1]
  const activeSectionCriteria = isNil(activeSection)
    ? []
    : criteriaForSection(criteriaRows, activeSection.id)
  const isEditingActiveSection = !isNil(activeSection) && editingSectionId === activeSection.id

  const handleAddSection = (name) => {
    const didAddSection = onAddSection(name)

    if (didAddSection !== false) {
      setActiveSectionId(null)
    }

    return didAddSection
  }

  const startEditingSection = (section) => {
    setEditingSectionId(section.id)
    setEditingSectionValue(section.name)
  }

  const commitSectionEdit = () => {
    if (!editingSectionId || !editingSectionValue.trim()) {
      return
    }

    const didUpdateSection = onUpdateSection(editingSectionId, editingSectionValue)

    if (didUpdateSection === false) {
      return
    }

    setEditingSectionId(null)
    setEditingSectionValue('')
  }

  const cancelSectionEdit = () => {
    setEditingSectionId(null)
    setEditingSectionValue('')
  }

  const startEditingCriterion = (criteriaRow) => {
    setCriterionSnapshot(criteriaRow)
    onCriteriaSelect(criteriaRow.rowId)
  }

  const confirmCriterionEdit = () => {
    setCriterionSnapshot(null)
    onCriteriaSelect(null)
  }

  const cancelCriterionEdit = (criteriaRow) => {
    if (criterionSnapshot?.rowId === criteriaRow.rowId) {
      onReplaceCriterion(criterionSnapshot)
    } else if (isNil(criterionSnapshot) && isCriteriaRowEmpty(criteriaRow)) {
      onRemoveCriterion(criteriaRow.rowId)
    }
    confirmCriterionEdit()
  }

  return (
    <div className="flex-column gap" data-testid="guided-step-custom">
      <div className="flex-align-center flex-space-between medium-gap">
        <p className="qa-copilot-guided-eyebrow uppercase muted-text bold no-margin">
          {[
            pluralize(sections.length, 'section', 'sections'),
            pluralize(criteriaRows.length, 'criterion', 'criteria'),
            `${sumPoints(criteriaRows)} pts`,
          ].join(' · ')}
        </p>
        <AddSectionInlineEntry onAdd={handleAddSection} disabled={Boolean(editingSectionId)} />
      </div>

      {isNil(activeSection) ? (
        <NoData
          muted
          padded
          headerText="No sections yet"
          text="Name your first section — for example Opening, Compliance or Payment Handling."
          data-testid="guided-structure-empty"
        />
      ) : (
        <>
          <div className="flex-wrap small-gap" aria-label="Sections">
            {sections.map((section) => (
              <Button
                key={section.id}
                basic
                small
                active={section.id === activeSection.id}
                onClick={() => setActiveSectionId(section.id)}
                data-testid={`guided-section-tab-${section.id}`}
              >
                {section.name}
                <Pill xs>{criteriaForSection(criteriaRows, section.id).length}</Pill>
              </Button>
            ))}
          </div>

          <Card compact className="qa-copilot-guided-panel flex-column medium-gap no-margin">
            {isEditingActiveSection ? (
              <div className="flex-align-center small-gap">
                <TextInput
                  autoFocus
                  className="flex-item"
                  value={editingSectionValue}
                  onChange={(event) => setEditingSectionValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      commitSectionEdit()
                    }
                    if (event.key === 'Escape') {
                      event.preventDefault()
                      cancelSectionEdit()
                    }
                  }}
                  data-testid={`guided-edit-section-input-${activeSection.id}`}
                />
                <Button small square basic type="button" onClick={cancelSectionEdit}>
                  <IconX />
                </Button>
                <Button
                  small
                  square
                  brand
                  type="button"
                  disabled={!editingSectionValue.trim()}
                  onClick={commitSectionEdit}
                >
                  <IconCheck />
                </Button>
              </div>
            ) : (
              <div className="flex-align-center small-gap">
                <Pill small>
                  {pluralize(activeSectionCriteria.length, 'criterion', 'criteria')}
                </Pill>
                <Pill small informative>{`${sumPoints(activeSectionCriteria)} pts`}</Pill>
                <span className="flex-item" />
                <Button
                  text
                  small
                  onClick={() => startEditingSection(activeSection)}
                  data-testid={`guided-edit-section-${activeSection.id}`}
                >
                  <IconPencil />
                  Rename section
                </Button>
                <GuidedDeleteButton
                  label="Delete section"
                  needsConfirmation={!isEmpty(activeSectionCriteria)}
                  onConfirm={() => onRemoveSection(activeSection)}
                  dataTestId={`guided-remove-section-${activeSection.id}`}
                />
              </div>
            )}
            <p className="muted-text small-text no-margin">
              Use the pencil on a criterion to edit its scoring detail
            </p>

            {isEmpty(activeSectionCriteria) ? (
              <NoData
                muted
                padded
                headerText="No criteria in this section"
                text='Add the first thing you want graded here — for example "Verified the policy number"'
              />
            ) : (
              <div className="flex-column small-gap">
                {activeSectionCriteria.map((criteriaRow) => {
                  const isExpanded = criteriaRow.rowId === selectedCriteriaRowId

                  return (
                    <Card
                      compact
                      key={criteriaRow.rowId}
                      className={classNames('qa-copilot-guided-criterion no-margin', {
                        expanded: isExpanded,
                      })}
                    >
                      <div className="flex-align-center small-gap">
                        <span className="flex-item text-overflow">
                          {criteriaRow.criterionName.trim() || 'Untitled Criterion'}
                        </span>
                        <Pill small>{`${parsePositiveInteger(criteriaRow.points)} pts`}</Pill>
                        {isExpanded ? (
                          <>
                            <Button
                              small
                              square
                              basic
                              aria-label="Cancel criterion changes"
                              tooltip="Cancel"
                              onClick={() => cancelCriterionEdit(criteriaRow)}
                              data-testid={`guided-criterion-cancel-${criteriaRow.rowId}`}
                            >
                              <IconX />
                            </Button>
                            <Button
                              small
                              square
                              brand
                              aria-label="Confirm criterion"
                              tooltip="Done"
                              disabled={!isCriteriaRowComplete(criteriaRow)}
                              onClick={confirmCriterionEdit}
                              data-testid={`guided-criterion-confirm-${criteriaRow.rowId}`}
                            >
                              <IconCheck />
                            </Button>
                          </>
                        ) : (
                          <Button
                            small
                            square
                            basic
                            aria-label="Edit criterion"
                            tooltip="Edit criterion"
                            onClick={() => startEditingCriterion(criteriaRow)}
                            data-testid={`guided-criterion-edit-${criteriaRow.rowId}`}
                          >
                            <IconPencil />
                          </Button>
                        )}
                        <GuidedDeleteButton
                          label="Delete criterion"
                          needsConfirmation={!isCriteriaRowEmpty(criteriaRow)}
                          onConfirm={() => onRemoveCriterion(criteriaRow.rowId)}
                          dataTestId={`guided-remove-criterion-${criteriaRow.rowId}`}
                        />
                      </div>

                      {isExpanded && (
                        <GuidedCriterionForm
                          criteriaRow={criteriaRow}
                          onUpdate={onUpdateSelectedCriteria}
                        />
                      )}
                    </Card>
                  )
                })}
              </div>
            )}

            <Button
              basic
              className="full-width"
              onClick={() => onAddCriterionToSection(activeSection.id)}
              data-testid={`guided-add-criterion-section-${activeSection.id}`}
            >
              <IconPlus />
              Add criterion
            </Button>
          </Card>
        </>
      )}

      {!isEmpty(validationError) && <ErrorMessage content={validationError} />}
    </div>
  )
}
